import { useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Loader2, Search, Copy, CheckCircle2, XCircle } from 'lucide-react';

export default function AdminDebugPMPage() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setLoading(true);
    setData(null);
    try {
      const res = await api.get(`/admin/debug/pm-visibility`, { params: { email: email.trim() } });
      setData(res.data);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Chiamata fallita', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const copyJson = () => {
    try {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const isActive = (pu) => {
    const ed = pu.end_date;
    if (ed == null) return true;
    if (typeof ed === 'string') return ed > new Date().toISOString();
    return false;
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-debug-pm-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Debug visibilità PM</h1>
        <p className="text-sm text-gray-500 mt-1">
          Analisi degli assignment `project_users` di un utente PM per capire perché non vede vendite/success fee.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Utente da analizzare</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={run} className="flex items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="email" className="text-xs">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="danielebonelli.vad@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="debug-pm-email"
                autoFocus
              />
            </div>
            <Button type="submit" disabled={loading || !email.trim()} data-testid="debug-pm-run">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Analizza
            </Button>
          </form>
        </CardContent>
      </Card>

      {data?.error && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-red-600 flex items-center gap-2"><XCircle className="w-5 h-5" /> {data.error}</div>
          </CardContent>
        </Card>
      )}

      {data && !data.error && (
        <>
          {/* Riepilogo */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Riepilogo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <Kpi label="Ruolo utente" value={data.user.role || (data.user.roles || []).join(', ') || '—'} />
                <Kpi label="Assignment totali" value={data.project_users_total} />
                <Kpi label="Attivi (filtro attuale)" value={data.active_project_users_matched} color={data.active_project_users_matched > 0 ? 'green' : 'red'} />
                <Kpi label="Progetti visibili" value={data.active_project_ids.length} />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm pt-2 border-t">
                <Kpi label="Sales visibili" value={data.sales_visible_count} color={data.sales_visible_count > 0 ? 'green' : 'orange'} />
                <Kpi label="Other Commissions visibili" value={data.other_commissions_visible_count} color={data.other_commissions_visible_count > 0 ? 'green' : 'orange'} />
              </div>

              {data.counters_breakdown && (
                <div className="pt-3 border-t space-y-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Breakdown avanzato</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    <Kpi label="Sales is_current=true" value={data.counters_breakdown.sales_current_true} />
                    <Kpi label="Sales is_current=false" value={data.counters_breakdown.sales_current_false} />
                    <Kpi label="Sales totali (progetti attivi)" value={data.counters_breakdown.sales_total_regardless} />
                    <Kpi label="OC is_current=true" value={data.counters_breakdown.oc_current_true} />
                    <Kpi label="OC is_current=false" value={data.counters_breakdown.oc_current_false} />
                    <Kpi label="OC totali (progetti attivi)" value={data.counters_breakdown.oc_total_regardless} />
                    <Kpi label="Progetti storici (chiusi)" value={data.counters_breakdown.historical_projects_count} />
                    <Kpi label="Sales su storici" value={data.counters_breakdown.historical_sales_current} color={data.counters_breakdown.historical_sales_current > 0 ? 'orange' : undefined} />
                    <Kpi label="OC su storici" value={data.counters_breakdown.historical_oc_current} color={data.counters_breakdown.historical_oc_current > 0 ? 'orange' : undefined} />
                  </div>
                </div>
              )}

              {data.sample_sale && (
                <details className="pt-3 border-t">
                  <summary className="text-xs font-semibold text-gray-500 uppercase cursor-pointer">Sample Sale record</summary>
                  <pre className="mt-2 text-[10px] bg-gray-50 p-3 rounded max-h-64 overflow-auto">{JSON.stringify(data.sample_sale, null, 2)}</pre>
                </details>
              )}
              {data.sample_oc && (
                <details className="pt-1">
                  <summary className="text-xs font-semibold text-gray-500 uppercase cursor-pointer">Sample Other Commission record</summary>
                  <pre className="mt-2 text-[10px] bg-gray-50 p-3 rounded max-h-64 overflow-auto">{JSON.stringify(data.sample_oc, null, 2)}</pre>
                </details>
              )}
              <div className="pt-2 text-xs text-gray-500">
                <b>Ora del server</b>: <code>{data.active_filter_now_iso}</code>
              </div>
            </CardContent>
          </Card>

          {/* Assignment table */}
          <Card>
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Assignment `project_users` ({data.project_users_all.length})</CardTitle>
              <Button variant="outline" size="sm" onClick={copyJson}>
                {copied ? <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copiato' : 'Copia JSON'}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left py-2 px-3">#</th>
                      <th className="text-left py-2 px-3">system_role</th>
                      <th className="text-left py-2 px-3">project_function</th>
                      <th className="text-left py-2 px-3">start_date</th>
                      <th className="text-left py-2 px-3">end_date (tipo)</th>
                      <th className="text-left py-2 px-3">Attivo?</th>
                      <th className="text-left py-2 px-3">project_id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.project_users_all.map((pu, idx) => {
                      const active = isActive(pu);
                      return (
                        <tr key={idx} className={`border-b ${active ? 'bg-green-50/40' : ''}`}>
                          <td className="py-2 px-3 text-gray-400">{idx + 1}</td>
                          <td className="py-2 px-3 font-medium">{pu.system_role || '—'}</td>
                          <td className="py-2 px-3">{pu.project_function || '—'}</td>
                          <td className="py-2 px-3 text-gray-500">{fmt(pu.start_date)}</td>
                          <td className="py-2 px-3">
                            <span className="text-gray-500">{fmt(pu.end_date)}</span>
                            <Badge variant="outline" className="ml-2 text-[9px]">{pu.end_date_type}</Badge>
                          </td>
                          <td className="py-2 px-3">
                            {active
                              ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> Sì</span>
                              : <span className="inline-flex items-center gap-1 text-red-600"><XCircle className="w-3.5 h-3.5" /> No</span>}
                          </td>
                          <td className="py-2 px-3 font-mono text-[10px] text-gray-400">{pu.project_id?.slice(0, 8)}…</td>
                        </tr>
                      );
                    })}
                    {data.project_users_all.length === 0 && (
                      <tr><td colSpan={7} className="py-6 text-center text-gray-400">Nessun assignment trovato per questo utente</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Progetti visibili */}
          {data.sample_projects?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Progetti visibili all'utente (primi {data.sample_projects.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm space-y-1">
                  {data.sample_projects.map((p, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="text-gray-400 font-mono text-[10px]">{p.id?.slice(0, 8)}…</span>
                      <span className="font-medium">{p.name}</span>
                      {p.status && <Badge variant="outline" className="text-[9px]">{p.status}</Badge>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, color }) {
  const colorClass = color === 'green' ? 'text-green-700' : color === 'red' ? 'text-red-600' : color === 'orange' ? 'text-orange-600' : 'text-gray-900';
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-0.5">{label}</div>
      <div className={`text-lg font-bold ${colorClass}`}>{value ?? '—'}</div>
    </div>
  );
}

function fmt(v) {
  if (v == null) return '—';
  if (typeof v === 'string' && v.length >= 10) return v.slice(0, 10);
  return String(v);
}
